import {
    Edit,
    useRecordContext,
    useDataProvider,
    useNotify,
    TopToolbar,
    useRedirect,
    Loading,
    RaRecord,
} from 'react-admin';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    IconButton,
    InputAdornment,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DoneIcon from '@mui/icons-material/Done';
import EditIcon from '@mui/icons-material/Edit';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useChartTheme, EMPTY, IntegralPair, IntegralResult, PairPoint } from './plotting';
import { ChannelChart } from './ChannelChart';
import { PickingToggle } from './PickingToggle';
import { formatMol } from './IntegralResults';
import { ChannelWorkflowNav } from './WorkflowNav';

const SAVE_DELAY = 800;

// The API stores whatever name it is sent, so uniqueness holds here. Counting
// off the list length collides after a deletion, so take the lowest free number.
const nextSampleName = (pairs: IntegralPair[]) => {
    const taken = new Set(pairs.map(pair => pair.sample_name));
    let n = 1;
    while (taken.has(`Sample ${n}`)) n++;
    return `Sample ${n}`;
};

const MyTopToolbar = () => {
    const redirect = useRedirect();
    const record = useRecordContext();

    if (!record || !record.id) {
        return null;
    }

    return (
        <TopToolbar>
            <Button
                variant="outlined"
                size="small"
                startIcon={<ArrowBackIcon />}
                onClick={() => redirect('show', 'instruments', record.experiment_id)}
            >
                Experiment
            </Button>
            <Button
                variant="outlined"
                size="small"
                startIcon={<ArrowBackIcon />}
                onClick={() => redirect('show', 'instrument_channels', record.id)}
            >
                Channel
            </Button>
        </TopToolbar>
    );
};

const DoneAction = ({ hasResults }: { hasResults: boolean }) => {
    const record = useRecordContext();
    const redirect = useRedirect();

    if (!record || !hasResults) {
        return null;
    }
    return (
        <Button
            variant="contained"
            size="small"
            startIcon={<DoneIcon />}
            onClick={() => redirect('show', 'instruments', record.experiment_id)}
        >
            Done
        </Button>
    );
};

const NoBaseline = ({ record }: { record: RaRecord }) => {
    const redirect = useRedirect();

    return (
        <>
            <ChannelWorkflowNav step={1} />
            <Alert
                severity="info"
                action={
                    <Button
                        color="inherit"
                        size="small"
                        onClick={() => redirect('edit', 'instrument_channels', record.id)}
                    >
                        Pick baseline points
                    </Button>
                }
            >
                Pick baseline points before integrating.
            </Alert>
        </>
    );
};

// Ranges live in component state rather than the record: a refetch
// mid-selection would discard a start point that has no end yet.
const IntegrationEditor = ({ record }: { record: RaRecord }) => {
    const dataProvider = useDataProvider();
    const notify = useNotify();
    const { colors } = useChartTheme();

    const [pairs, setPairs] = useState<IntegralPair[]>(() => record.integral_chosen_pairs ?? []);
    const [results, setResults] = useState<IntegralResult[]>(() => record.integral_results ?? []);
    const [savingRow, setSavingRow] = useState<number | null>(null);
    const [savedRow, setSavedRow] = useState<number | null>(null);

    // A channel that already has ranges opens for reading, not extending
    const [adding, setAdding] = useState(
        () => (record.integral_chosen_pairs ?? []).length === 0,
    );

    // Read by the plot click handler and the debounced save, both of which can
    // run against an older render
    const pairsRef = useRef(pairs);
    pairsRef.current = pairs;
    const renameTimer = useRef<ReturnType<typeof setTimeout>>();
    const renamePending = useRef(false);

    // Queued rather than parallel: two saves in flight can resolve out of
    // order and the older response would show stale integrals
    const queue = useRef<Promise<unknown>>(Promise.resolve());
    const persist = useCallback((next: IntegralPair[]) => {
        queue.current = queue.current
            .catch(() => undefined)
            .then(() => dataProvider.update('instrument_channels', {
                id: record.id,
                data: { integral_chosen_pairs: next },
                previousData: record,
            }))
            .then(({ data }) => {
                setResults(data.integral_results ?? []);
                return data;
            })
            .catch((error) => {
                notify('Error: Changes could not be saved');
                console.error(error);
            });
        return queue.current;
    }, [dataProvider, record, notify]);

    // A rename still waiting out its delay would be lost on navigation
    useEffect(() => () => {
        clearTimeout(renameTimer.current);
        if (renamePending.current) {
            dataProvider
                .update('instrument_channels', {
                    id: record.id,
                    data: { integral_chosen_pairs: pairsRef.current },
                    previousData: record,
                })
                .catch((error) => console.error(error));
        }
    }, [dataProvider, record]);

    const handlePlotClick = (point: PairPoint) => {
        const current = pairsRef.current;
        const last = current[current.length - 1];
        const completing = last && !last.end;
        const next = completing
            ? [...current.slice(0, -1), { ...last, end: point }]
            : [...current, { start: point, sample_name: nextSampleName(current) }];

        setPairs(next);
        if (completing) {
            persist(next);
        }
    };

    const renameSample = (index: number, value: string) => {
        setPairs(pairsRef.current.map((pair, position) => (
            position === index ? { ...pair, sample_name: value } : pair
        )));
        setSavingRow(index);
        setSavedRow(null);
        renamePending.current = true;
        clearTimeout(renameTimer.current);
        renameTimer.current = setTimeout(() => {
            persist(pairsRef.current).then(() => {
                renamePending.current = false;
                setSavingRow(null);
                setSavedRow(index);
            });
        }, SAVE_DELAY);
    };

    const removePair = (index: number) => {
        const next = pairsRef.current.filter((_, position) => position !== index);
        setPairs(next);
        setSavingRow(null);
        setSavedRow(null);
        persist(next);
    };

    const resultFor = (pair: IntegralPair) => results.find(result => (
        pair.end && result.start === pair.start.x && result.end === pair.end.x
    ));

    return (
        <Box sx={{ width: '100%' }}>
            <ChannelWorkflowNav
                step={2}
                hasResults={results.length > 0}
                hint="Mark each peak with two clicks, a start and an end, and name the range below. Turn on Edit ranges to change the selection."
                action={<DoneAction hasResults={results.length > 0} />}
            />

            <Stack
                direction="row"
                justifyContent="flex-end"
                alignItems="center"
                spacing={1.5}
                sx={{ mb: -1 }}
            >
                {adding && (
                    <Typography variant="caption" color="text.secondary">
                        Click anywhere to add the point in the tooltip, hover a range end and click it to remove
                    </Typography>
                )}
                <PickingToggle
                    adding={adding}
                    onToggle={() => setAdding(!adding)}
                    addLabel="Edit ranges"
                    stopLabel="Stop editing"
                    icon={<EditIcon />}
                />
            </Stack>

            <ChannelChart
                title={record.channel_name}
                time={record.time_values}
                raw={record.raw_values ?? EMPTY}
                corrected={record.baseline_values}
                pickFrom="corrected"
                pickingEnabled={adding}
                onPick={handlePlotClick}
                bands={pairs}
                onRemoveBand={removePair}
                hidden={['raw']}
            />

            <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Integration ranges</Typography>
                {pairs.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                        No ranges yet. Click a start point on the plot.
                    </Typography>
                ) : (
                    <Table size="small" sx={{ '& .MuiTableCell-root': { py: 0.5 } }}>
                        <TableHead>
                            <TableRow>
                                <TableCell padding="checkbox" />
                                <TableCell sx={{ width: 260 }}>Sample</TableCell>
                                <TableCell>Start [s]</TableCell>
                                <TableCell>End [s]</TableCell>
                                <TableCell>Electrons transferred [mol]</TableCell>
                                <TableCell padding="checkbox" />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {pairs.map((pair, index) => {
                                const result = resultFor(pair);
                                return (
                                    <TableRow key={pair.start.x}>
                                        <TableCell padding="checkbox">
                                            <Box sx={{
                                                width: 12,
                                                height: 12,
                                                borderRadius: '2px',
                                                bgcolor: colors.fills[index % colors.fills.length],
                                            }} />
                                        </TableCell>
                                        <TableCell>
                                            <TextField
                                                value={pair.sample_name ?? ''}
                                                onChange={event => renameSample(index, event.target.value)}
                                                variant="outlined"
                                                margin="none"
                                                size="small"
                                                fullWidth
                                                sx={{ '& .MuiInputBase-input': { py: 0.5 } }}
                                                InputProps={{
                                                    endAdornment: (
                                                        <InputAdornment position="end">
                                                            {savingRow === index && <CircularProgress size={14} />}
                                                            {savedRow === index && <DoneIcon fontSize="small" color="success" />}
                                                        </InputAdornment>
                                                    ),
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell>{pair.start.x}</TableCell>
                                        <TableCell>
                                            {pair.end
                                                ? pair.end.x
                                                : <Typography variant="body2" color="text.secondary">Click the end point on the plot</Typography>}
                                        </TableCell>
                                        <TableCell>
                                            {result ? formatMol(result.area) : '-'}
                                        </TableCell>
                                        <TableCell padding="checkbox">
                                            <IconButton size="small" aria-label="Remove range" onClick={() => removePair(index)}>
                                                <DeleteOutlineIcon fontSize="small" />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                )}
            </Paper>
        </Box>
    );
};

const IntegrationGate = () => {
    const record = useRecordContext();

    if (!record) {
        return <Loading />;
    }
    if (!(record.time_values ?? []).length || !(record.baseline_values ?? []).length) {
        return <NoBaseline record={record} />;
    }
    return <IntegrationEditor key={record.id} record={record} />;
};

const InstrumentChannelIntegrate = () => (
    <Edit actions={<MyTopToolbar />}>
        <Box sx={{ p: 2 }}>
            <IntegrationGate />
        </Box>
    </Edit>
);

export default InstrumentChannelIntegrate;
